import { Alert, Button, Spin } from 'antd'
import { FieldValueLookupProvider } from 'features/libra/fieldValues/FieldValueLookupContext'
import { UseWorkflowFieldValuesReturn } from 'features/libra/fieldValues/useWorkflowFieldValues'
import {
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { renderSection } from 'features/libra/sections/SectionRegistry'
import { applyAiFieldValues } from 'features/libra/stepComponents/applyAiFieldValues'
import { AiAutofillBanner } from 'features/libra/stepComponents/GongAgentAutofill'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoNotifications } from 'features/notifications'
import { FC, Suspense, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import cantileverTheme from 'styles/cantileverTheme'

import { ConditionalFormConfig } from './api/conditionalFormApi'
import { isConditionalFormStepComplete } from './conditionalFormStepCompletion'

const AgentSectionBanner: FC<{
  section: { id: string; agentPermissions?: string[] }
  setFieldValues: (
    updates: FieldValues | ((prev: FieldValues) => FieldValues)
  ) => void
}> = ({ section, setFieldValues }) => {
  const onFieldValues = useCallback(
    (fv: FieldValues) => setFieldValues((prev) => applyAiFieldValues(prev, fv)),
    [setFieldValues]
  )
  return <AiAutofillBanner section={section} onFieldValues={onFieldValues} />
}

export const ConditionalFormView = ({
  config,
  ontologyFields,
  formState,
  readonly,
}: {
  config: ConditionalFormConfig
  ontologyFields: Record<string, OntologyField>
  formState: UseWorkflowFieldValuesReturn
  readonly: boolean
}) => {
  const stepId = useActiveStepId()
  const { globalContext } = useWorkflow()
  const {
    resolvedFieldValues,
    initialFieldValues,
    drawingResetKey,
    setFieldValues,
    allVisibleSections,
    hasUnsavedEdits,
    hasUnsavedEditsOrSystemValues,
    handleCancel,
    handleSave,
    isLoading,
  } = formState

  const saveGuard = useRegisterSaveGuard({
    hasUnsavedEdits: !readonly && hasUnsavedEdits,
    hasUnsavedEditsOrSystemValues: !readonly && hasUnsavedEditsOrSystemValues,
    save: handleSave,
  })
  const { errorNotification } = useGeckoNotifications()
  const handleBannerSave = useCallback(async () => {
    try {
      await handleSave()
    } catch {
      errorNotification('Unable to save changes. Your edits are still here.')
    }
  }, [errorNotification, handleSave])

  const isStepComplete = useMemo(() => {
    return isConditionalFormStepComplete({
      config,
      fieldValues: resolvedFieldValues,
      globalContext,
    })
  }, [config, resolvedFieldValues, globalContext])

  useReportValidation(
    readonly || isStepComplete
      ? undefined
      : {
          status: 'Error',
          message: 'Step incomplete - please fill in all required fields',
        }
  )

  return (
    <FormContainer>
      <SaveGuardModal {...saveGuard} />
      {!readonly && (
        <StickySaveBannerContainer>
          <StyledAlert
            title={
              hasUnsavedEditsOrSystemValues ? 'Save Changes?' : 'No Changes'
            }
            type={hasUnsavedEditsOrSystemValues ? 'warning' : 'success'}
            showIcon
            action={
              hasUnsavedEditsOrSystemValues && (
                <BannerActions>
                  <CancelButton
                    type='dashed'
                    size='small'
                    disabled={isLoading}
                    onClick={handleCancel}>
                    Cancel
                  </CancelButton>
                  <SaveButton
                    type='primary'
                    size='small'
                    disabled={isLoading}
                    onClick={handleBannerSave}>
                    Save
                  </SaveButton>
                </BannerActions>
              )
            }
          />
        </StickySaveBannerContainer>
      )}
      <FieldValueLookupProvider config={config.lookups}>
        {allVisibleSections.map((section) => {
          return (
            <SectionBlock key={section.id}>
              {section.title && <SectionTitle>{section.title}</SectionTitle>}
              {'agent' in section &&
                section.agent &&
                section.type !== 'zones' &&
                section.type !== 'consequence_of_failure' &&
                !readonly && (
                  <AgentSectionBanner
                    section={section}
                    setFieldValues={setFieldValues}
                  />
                )}
              <Suspense fallback={<Spin />}>
                {renderSection(section.type, {
                  config: section,
                  stepId,
                  ontologyFields,
                  fieldValues: resolvedFieldValues,
                  initialFieldValues,
                  setFieldValues: readonly ? undefined : setFieldValues,
                  drawingResetKey,
                })}
              </Suspense>
            </SectionBlock>
          )
        })}
      </FieldValueLookupProvider>
    </FormContainer>
  )
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding-bottom: 1rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  }
`

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.activeText};
`

const StickySaveBannerContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${({ theme }) => theme.forecast.background1};
`

const StyledAlert = styled(Alert)`
  min-height: 48px;

  .ant-alert-content {
    display: flex;
    align-items: center;
  }
`

const BannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CancelButton = styled(Button)`
  background-color: ${cantileverTheme.colors.grayscale['500']} !important;
`

const SaveButton = styled(Button)`
  background-color: ${cantileverTheme.colors.OceanBlue} !important;
`
